import React, { memo, useState, useCallback, useMemo, useEffect } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { QuickBooksStatus } from './QuickBooksStatus';
import { QuickBooksConfig } from './QuickBooksConfig';
import { QuickBooksLog } from './QuickBooksLog';
import { getQuickBooksAuthStatus, extractQbError } from './actions';
import type { QuickBooksAuthStatus as AuthStatusType } from './types';

import classes from './quickBooks.module.css';

interface QuickBooksContainerProps {
  locationId: string;
  ssoToken: string;
  isAdmin?: boolean;
}

interface TabConfig {
  id: string;
  label: string;
  requiresAuth?: boolean;
  adminOnly?: boolean;
}

const allTabs: TabConfig[] = [
  { id: 'status', label: 'Connection Status' },
  { id: 'config', label: 'Configuration', requiresAuth: true },
  { id: 'log', label: 'Activity Log', requiresAuth: true },
];

function QuickBooksContainer({ locationId, ssoToken, isAdmin = false }: QuickBooksContainerProps) {
  const [activeTab, setActiveTab] = useState<string>('status');
  // Single source of truth for connection state — the Status tab reads/refreshes
  // this same state instead of keeping its own (prevents the Config/Log tabs from
  // going stale after connect/disconnect).
  const [authStatus, setAuthStatus] = useState<AuthStatusType | null>(null);
  const [checkingAuth, setCheckingAuth] = useState<boolean>(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const isAuthenticated = Boolean(authStatus?.connected);

  const refreshAuth = useCallback(async () => {
    try {
      setCheckingAuth(true);
      setAuthError(null);
      const status = await getQuickBooksAuthStatus(locationId, ssoToken);
      setAuthStatus(status);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error checking QuickBooks auth:', err);
      setAuthStatus(null);
      // Surface the failure so the Status tab shows an error/retry state instead
      // of silently falling through to the "Not Connected" UI.
      setAuthError(extractQbError(err, 'Failed to check QuickBooks connection status'));
    } finally {
      setCheckingAuth(false);
    }
  }, [locationId, ssoToken]);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  // Filter tabs based on auth status and admin role
  const tabs = useMemo(() => allTabs.filter((tab) => {
      if (tab.adminOnly && !isAdmin) return false;
      if (tab.requiresAuth && !isAuthenticated) return false;
      return true;
    }), [isAuthenticated, isAdmin]);

  // Make sure active tab is valid
  useEffect(() => {
    const isValidTab = tabs.some((tab) => tab.id === activeTab);
    if (!isValidTab && tabs.length > 0) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  const handleTabClick = useCallback((tabId: string) => {
    setActiveTab(tabId);
  }, []);

  // Initial load — full-screen spinner only before the first result arrives.
  if (checkingAuth && authStatus === null) {
    return (
      <div className={classes.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 text-muted">Checking QuickBooks status...</p>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* Tab Navigation */}
      <div className={classes.tabNav}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`${classes.tab} ${activeTab === tab.id ? classes.tabActive : ''}`}
            onClick={() => handleTabClick(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className={classes.tabContent}>
        {activeTab === 'status' && (
          <QuickBooksStatus
            locationId={locationId}
            ssoToken={ssoToken}
            authStatus={authStatus}
            authError={authError}
            loading={checkingAuth}
            onAuthChange={refreshAuth}
          />
        )}
        {activeTab === 'config' && isAuthenticated && (
          <QuickBooksConfig
            locationId={locationId}
            ssoToken={ssoToken}
            onConfigSaved={refreshAuth}
          />
        )}
        {activeTab === 'log' && isAuthenticated && (
          <QuickBooksLog
            locationId={locationId}
            ssoToken={ssoToken}
          />
        )}
      </div>
    </div>
  );
}

const QuickBooksContainerMemo = memo(QuickBooksContainer, areEqual);

export { QuickBooksContainerMemo as QuickBooksContainer };
